import type { FindingCounts, QuestionnaireSnapshot } from '@nexus/core';
import type { OrganizationDetails } from '@nexus/mod-crm';
import type { ObjectDetails, ProjectDetails } from '@nexus/mod-projects';

/** Planned, started on site, or completed with the report sent ("Protokoll versendet"). */
export const INSPECTION_STATUSES = ['planned', 'in-progress', 'completed'] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

export const INSPECTION_TITLE_MAX = 200;
export const MAX_INSPECTORS = 20;
export const PARTICIPANTS_MAX = 2000;
export const MAX_ATTENDEES = 100;
export const ATTENDEE_TEXT_MAX = 200;
export const REMARKS_MAX = 20_000;
export const MAX_ATTACHMENTS = 50;
export const ATTACHMENT_TITLE_MAX = 200;

/** Someone present at the inspection, e.g. the site manager of a contractor. */
export interface Attendee {
  name: string;
  /** Function on site, e.g. „Bauleiter Rohbau“. */
  role: string | null;
}

/** A PDF file of the inspection the report appends, in this order. */
export interface InspectionAttachment {
  /** File of the kernel with the entity `sifa.inspection` and the inspection as record. */
  fileId: string;
  /** Shown in the report; without it the file name. */
  title: string | null;
}

/** Project of the inspection as far as the person may see it in the module projects. */
export interface InspectionProject {
  id: string;
  number: string;
  title: string;
}

/** Object of the inspection as far as the person may see it in the module projects. */
export interface InspectionObject {
  id: string;
  number: string | null;
  name: string;
}

/** Inspection in the list. */
export interface InspectionListItem {
  id: string;
  projectId: string;
  objectId: string | null;
  /** `null` when the person may not see the project or object in projects. */
  project: InspectionProject | null;
  object: InspectionObject | null;
  title: string;
  /** ISO date (YYYY-MM-DD). */
  inspectedOn: string;
  status: InspectionStatus;
  /** When the report was marked as sent. */
  completedAt: string | null;
  inspectorIds: string[];
  findings: FindingCounts;
  updatedAt: string;
  version: number;
}

/** Inspection as the API returns it. */
export interface Inspection extends InspectionListItem {
  participants: string | null;
  attendees: Attendee[];
  remarks: string | null;
  questionnaire: QuestionnaireSnapshot | null;
  attachments: InspectionAttachment[];
  createdBy: string | null;
  /**
   * Write permission and scope cover the inspection. Completed inspections stay unchanged until
   * someone with this right reopens them.
   */
  canWrite: boolean;
  createdAt: string;
}

/**
 * `data` of an inspection on the device (offline source `sifa.inspection`). Project and object
 * come from the offline data of projects, findings from the kernel.
 */
export interface InspectionOfflineData {
  projectId: string;
  objectId: string | null;
  title: string;
  inspectedOn: string;
  status: InspectionStatus;
  completedAt: string | null;
  inspectorIds: string[];
  participants: string | null;
  attendees: Attendee[];
  remarks: string | null;
  questionnaire: QuestionnaireSnapshot | null;
  attachments: InspectionAttachment[];
  createdBy: string | null;
}

/** Longest bullet points for an AI draft of a finding. */
export const DRAFT_TEXT_MAX = 4000;

/**
 * What the report of an inspection needs besides the inspection, as far as the person may see it
 * in projects and crm. `GET /api/sifa/inspections/:id/report`.
 */
export interface InspectionReportData {
  inspection: Inspection;
  project: ProjectDetails | null;
  object: ObjectDetails | null;
  /** Client of the project, else operator, else owner of the object. */
  customer: OrganizationDetails | null;
  /** Name of the client's contact person on the project; only when the customer is the client. */
  contactName: string | null;
  /** Names of the inspectors in their order; `null` for erased accounts. */
  inspectors: { id: string; name: string | null }[];
}

/** Shortest and longest search term for the substance search. */
export const SUBSTANCE_QUERY_MIN = 2;
export const SUBSTANCE_QUERY_MAX = 100;
/** Languages of the GESTIS substance database. */
export const SUBSTANCE_LANGUAGES = ['de', 'en'] as const;
export type SubstanceLanguage = (typeof SUBSTANCE_LANGUAGES)[number];

/** A substance of the GESTIS substance database of the IFA, with the link to its data sheet. */
export interface Substance {
  name: string;
  casNumber: string | null;
  /** ZVG number of GESTIS. */
  zvgNumber: string;
  url: string;
}
